/* ============================================================
   AEPERION — Cart

   ------------------------------------------------------------
   WHAT THIS IS, EXACTLY

   A working cart with no merchant behind it. Lines are held in
   localStorage, the drawer and /cart.html render from that, and the
   Checkout button declines — in words, on screen — because there is
   nothing on the other end of it. Every number shown is arithmetic on
   data the page itself published; none of it is invented.

   That boundary is the whole design. A demonstration cart that mimed a
   checkout would be lying at the one moment a visitor is deciding
   whether to trust the shop, and this site's entire argument (see the
   header of shell.js) is that it does not lie about state.

   ------------------------------------------------------------
   THE SHOPIFY BACKEND (the port this header always promised)

   On a storefront — detected by `window.Shopify.routes`, which every
   storefront defines — the four store mutations talk to the Cart AJAX
   API instead, and the server owns every number:

     boot        → GET  cart.js
     addItem()   → POST cart/add.js, then GET cart.js
     setQty()    → POST cart/change.js   { id: key, quantity }
     removeItem()→ POST cart/change.js   { id: key, quantity: 0 }

   Each still ends in publish(), which dispatches `cart:updated` on
   document — the event shell.js listens for to move the header count,
   and the event the drawer re-renders from. Nothing downstream knows
   which backend fired it, which is why the paragraph above about the
   demonstration build stays true exactly where it applies and stops
   applying the moment a merchant exists.

   ------------------------------------------------------------
   RULES THIS FILE HOLDS ITSELF TO

   1. It never gates content. The drawer is built lazily, on first open.
      If this module throws on line one, the header cart is still a link
      to /cart.html and every page still renders.
   2. Money is integer cents everywhere but the moment it is printed.
      Floating-point dollars accumulate error across a quantity change,
      and a subtotal that is off by a cent is the most expensive
      possible place to be sloppy.
   3. The exit animation is driven from JS, not from `transition-behavior:
      allow-discrete` on the dialog. Discrete transitions on `display`
      and `overlay` are the tidier stylesheet, but a browser without
      `overlay` support drops the panel out of the top layer the instant
      close() lands and paints the exit underneath the page. A class and
      a transitionend are deterministic in every engine that has
      <dialog> at all.
   4. Monochrome, one curve (--e-glide), nothing overshoots — the same
      contract as every other stylesheet here.
   ============================================================ */
/* stylesheet moved to Liquid: cart */
import { reduced } from '@aep/motion';

/* ============================================================
   Backend selection — the swap the header above always promised.

   On a Shopify storefront `window.Shopify.routes.root` always exists, so
   its presence IS the feature test: with it, the four store mutations
   below talk to the Cart AJAX API and the server owns every number; on
   the static demo they keep the localStorage store. Everything
   downstream — the drawer, the header count, the cart page — subscribes
   to `cart:updated` and never learns which backend fired it.

   The URLs the drawer links out to move with the backend for the same
   reason the prices do: /shop.html and /cart.html do not exist on a
   storefront, and a checkout only exists where a merchant does.
   ============================================================ */
const SHOPIFY = typeof window !== 'undefined'
  && !!(window.Shopify && window.Shopify.routes && window.Shopify.routes.root);
const ROOT = SHOPIFY ? window.Shopify.routes.root : '/';

export const urls = {
  shop: SHOPIFY ? ROOT + 'collections/all' : '/shop.html',
  cart: SHOPIFY ? ROOT + 'cart' : '/cart.html',
  /** null on the demo build — the callers keep their honest decline. */
  checkout: SHOPIFY ? ROOT + 'checkout' : null,
};

const KEY = 'aep-cart';

/** Bump when the stored shape changes; anything older is dropped, not migrated. */
const SCHEMA = 1;

/** Per-line ceiling. Matches the `max` on the PDP quantity input. */
const MAX_QTY = 10;

/* ============================================================
   Store

   One array of lines, keyed by variant id. Prices are cents.
   ============================================================ */

/** @typedef {{id:string, sku:string, title:string, size:string, dims:string,
 *             url:string, image:string, cents:number, qty:number}} Line */

/** @type {Line[]|null} in-memory copy, so a render never parses JSON. */
let lines = null;

const cents = (v) => Math.round(Number(v) * 100) || 0;

/* The one place money is formed. Everything upstream is integers.

   The currency is the ACTIVE MARKET's, read off `Shopify.currency` — the same
   value the storefront resolves the cart in — and never typed. This used to
   emit a literal `$` with two decimals, which printed `$7300.00` to a visitor
   whose cart Shopify was charging in yen.

   Intl is what makes that correct for more than the symbol: JPY has no minor
   unit, so `toFixed(2)` was inventing decimals that do not exist, and the
   thousands separator is a locale decision this file has no business making.

   Shopify sends every amount in the currency's subunit, JPY included (¥7,300
   arrives as 730000), so dividing by 100 before formatting is right for all
   of them. */
const CURRENCY =
  (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

let cartFormat = null;
try {
  cartFormat = new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: CURRENCY,
  });
} catch {
  /* an engine without Intl, or a currency code it does not know */
}

export const money = (c) =>
  cartFormat ? cartFormat.format(c / 100) : `${CURRENCY} ${(c / 100).toFixed(2)}`;

const clampQty = (n) => Math.min(MAX_QTY, Math.max(1, Math.floor(Number(n) || 1)));

/**
 * A line's total in cents. Server-priced lines carry `line_cents` because
 * a per-unit discount can round differently than unit × qty; local lines
 * have no discounts, so arithmetic is exact.
 */
export const lineTotal = (l) => (l.line_cents == null ? l.cents * l.qty : l.line_cents);

/* ============================================================
   The Shopify backend. The server's cart is the only copy; these map its
   responses into the same Line shape the local store uses, so every
   renderer stays backend-blind.
   ============================================================ */

let srvCount = 0;
let srvTotal = 0;

/** Ask the CDN for a thumbnail-sized derivative instead of the master. */
const sized = (src, w) => (src ? src + (src.includes('?') ? '&' : '?') + 'width=' + w : '');

function lineFromShopify(item) {
  const variant = item.variant_title && item.variant_title !== 'Default Title'
    ? String(item.variant_title) : '';
  return {
    id: String(item.key || item.id),
    sku: String(item.sku || ''),
    title: String(item.product_title || item.title || 'Item'),
    size: variant,
    dims: '',
    url: String(item.url || ''),
    image: sized(item.image ? String(item.image) : '', 480),
    cents: Math.max(0, Math.floor(Number(item.final_price) || 0)),
    line_cents: Math.max(0, Math.floor(Number(item.final_line_price) || 0)),
    qty: Math.max(1, Math.floor(Number(item.quantity) || 1)),
  };
}

function applyShopify(cart) {
  lines = (cart.items || []).map(lineFromShopify);
  srvCount = Math.max(0, Number(cart.item_count) || 0);
  srvTotal = Math.max(0, Number(cart.total_price) || 0);
}

async function shopifyRequest(path, body) {
  const res = await fetch(ROOT + path, {
    method: body ? 'POST' : 'GET',
    headers: body
      ? { 'Content-Type': 'application/json', Accept: 'application/json' }
      : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    // 422 carries the merchant-facing reason — "All 2 XXL are in your cart".
    let reason = '';
    try { reason = (await res.json()).description || ''; } catch { /* not JSON */ }
    throw new Error(reason || 'the cart did not respond');
  }
  return res.json();
}

async function refreshShopify() {
  applyShopify(await shopifyRequest('cart.js'));
}

/**
 * Accept only lines we could have written ourselves. localStorage is
 * user-writable and shared with every other script on the origin, so a
 * malformed or hostile entry has to fail closed rather than reach the
 * DOM — `title` and `image` are both interpolated into the drawer.
 */
function sane(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').slice(0, 64);
  const c = Math.max(0, Math.floor(Number(raw.cents) || 0));
  if (!id || !c) return null;

  // Images are same-origin paths from our own markup. Anything else —
  // a data: URI, an absolute URL, a protocol-relative one — is not ours.
  //
  // `%` is in the set because it has to be: the responsive images are
  // named quartz-01@640.webp, and the hosted build percent-encodes that
  // "@" to save a redirect at the edge (see scripts/rebase.mjs). Without
  // it every thumbnail failed this check and the drawer rendered lines
  // with no picture — silently, because failing closed is the whole point
  // of this function. Still no ".." and still no scheme, which is what it
  // is actually defending against.
  const image = typeof raw.image === 'string' && /^\/[\w./@%-]*$/.test(raw.image) ? raw.image : '';
  const url = typeof raw.url === 'string' && /^\/[\w./%-]*$/.test(raw.url) ? raw.url : '';

  return {
    id,
    sku: String(raw.sku || '').slice(0, 64),
    title: String(raw.title || 'Item').slice(0, 80),
    size: String(raw.size || '').slice(0, 24),
    dims: String(raw.dims || '').slice(0, 48),
    url,
    image,
    cents: c,
    qty: clampQty(raw.qty),
  };
}

function load() {
  if (lines) return lines;
  lines = [];
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (parsed && parsed.v === SCHEMA && Array.isArray(parsed.items)) {
      lines = parsed.items.map(sane).filter(Boolean);
    }
  } catch {
    lines = [];                       // private mode, or somebody else's data
  }
  return lines;
}

function save() {
  try {
    localStorage.setItem(KEY, JSON.stringify({ v: SCHEMA, items: lines }));
  } catch { /* private mode: the cart is still live for this page's life */ }
}

/** The public shape. Deliberately Shopify's, minus the fields nothing reads. */
export function readCart() {
  if (SHOPIFY) {
    return {
      items: (lines || []).map((l) => ({ ...l, line_price: lineTotal(l) })),
      item_count: srvCount,
      total_price: srvTotal,
    };
  }
  const items = load();
  return {
    items: items.map((l) => ({ ...l, line_price: lineTotal(l) })),
    item_count: items.reduce((n, l) => n + l.qty, 0),
    total_price: items.reduce((n, l) => n + lineTotal(l), 0),
  };
}

/**
 * Announce a change. The single fan-out point: shell.js moves the header
 * count from here, the drawer re-renders from here, /cart.html repaints
 * from here, and a second tab picks it up through `storage` below.
 * @param {string} reason  'add' | 'qty' | 'remove' | 'sync' | 'boot'
 */
function publish(reason) {
  const cart = readCart();
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { ...cart, reason } }));
  return cart;
}

/**
 * Async on both backends so callers hold one contract; on the local store
 * the promise resolves in a microtask. A Shopify failure REJECTS — the
 * store still holds the truth, nothing was optimistically painted, and
 * the caller owns the apology (see initAddToCart in product.js).
 *
 * @param {object} line  everything but qty — see the Line typedef.
 *   The Shopify backend reads only `id`: the server prices the line, which
 *   is the point — a tampered data-price attribute buys nothing.
 * @param {number} qty
 */
export async function addItem(line, qty = 1) {
  const add = clampQty(qty);

  if (SHOPIFY) {
    await shopifyRequest('cart/add.js', {
      items: [{ id: Number(line.id), quantity: add }],
    });
    // add.js returns only the touched lines; the cart is one GET away.
    await refreshShopify();
    return publish('add');
  }

  const items = load();
  const existing = items.find((l) => l.id === String(line.id));

  if (existing) {
    existing.qty = clampQty(existing.qty + add);
  } else {
    const next = sane({ ...line, qty: add });
    if (!next) return readCart();
    items.push(next);
  }

  save();
  return publish('add');
}

export async function setQty(id, qty) {
  const next = Math.floor(Number(qty) || 0);
  if (next <= 0) return removeItem(id);

  if (SHOPIFY) {
    // change.js answers with the whole cart — including a quantity the
    // server clamped to stock, which is why the response is applied
    // rather than the request being assumed.
    applyShopify(await shopifyRequest('cart/change.js', { id: String(id), quantity: clampQty(next) }));
    return publish('qty');
  }

  const items = load();
  const line = items.find((l) => l.id === String(id));
  if (!line) return readCart();

  line.qty = clampQty(next);
  save();
  return publish('qty');
}

export async function removeItem(id) {
  if (SHOPIFY) {
    applyShopify(await shopifyRequest('cart/change.js', { id: String(id), quantity: 0 }));
    return publish('remove');
  }

  const items = load();
  const i = items.findIndex((l) => l.id === String(id));
  if (i === -1) return readCart();
  items.splice(i, 1);
  save();
  return publish('remove');
}

export async function clearCart() {
  if (SHOPIFY) {
    applyShopify(await shopifyRequest('cart/clear.js', {}));
    return publish('remove');
  }
  lines = [];
  save();
  return publish('remove');
}

/**
 * Another tab changed the cart. Drop the in-memory copy and republish, so
 * two open windows never disagree about what is in it.
 */
function watchStorage() {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    lines = null;
    publish('sync');
  });
}

/* ============================================================
   Reading a line off the page

   The PDP already publishes every field a line needs, as data attributes
   on the variant radio and as the gallery frame that matches it. Reading
   them beats a catalogue constant here for the usual reason: there is
   then exactly one copy of the price, and it is the one the customer
   was looking at when they pressed the button.
   ============================================================ */

/**
 * The narrowest candidate in a srcset. The drawer paints a 64px thumb;
 * handing it the 1200px master because that is what `src` says is 60 kB
 * of decode for 4 kB of picture.
 */
function thumbFrom(img) {
  if (!img) return '';
  const set = img.getAttribute('srcset') || '';
  const best = set
    .split(',')
    .map((part) => part.trim().split(/\s+/))
    .filter(([url]) => url)
    .map(([url, d]) => ({ url, w: parseInt(d, 10) || Infinity }))
    .sort((a, b) => a.w - b.w)[0];
  return best ? best.url : img.getAttribute('src') || '';
}

/**
 * Build a cart line from the PDP's own markup.
 * @param {HTMLInputElement} input the checked .sizes__input
 * @returns {object|null}
 */
export function lineFromVariant(input) {
  if (!input) return null;
  const form = input.closest('[data-cart-form]');
  const d = input.dataset;

  // The gallery frame tagged with this variant's media key, so the thumb
  // in the drawer is the photograph the customer just chose.
  const fig = document.querySelector(`[data-gallery] figure[data-media="${CSS.escape(d.media || '')}"]`)
    || document.querySelector('[data-gallery] figure');

  return {
    id: input.value,
    sku: d.sku || '',
    title: (form && form.dataset.productTitle) || document.title.split('—')[0].trim(),
    size: d.size || '',
    dims: d.dims || '',
    url: (form && form.dataset.productUrl) || window.location.pathname,
    image: thumbFrom(fig && fig.querySelector('img')),
    cents: cents(d.price),
  };
}

/* ============================================================
   The drawer

   Built on first open, never before: a visitor who reads one page and
   leaves pays nothing for it. <dialog>.showModal() is doing the work
   that would otherwise be 60 lines of focus trap, inert background,
   Escape handling and top-layer z-index guesswork.
   ============================================================ */

let dlg = null;
let panel = null;
let listEl = null;
let emptyEl = null;
let countEl = null;
let subEl = null;
let statusEl = null;
let footEl = null;
let opener = null;
let closeTimer = 0;
let open = false;

const esc = (s) =>
  String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function build() {
  if (dlg) return;

  dlg = document.createElement('dialog');
  dlg.className = 'aep-drawer';
  dlg.setAttribute('aria-label', 'Cart');

  dlg.innerHTML = `
    <div class="aep-drawer__scrim" data-cart-dismiss></div>
    <div class="aep-drawer__panel">
      <header class="aep-drawer__head">
        <p class="label aep-drawer__title">Cart</p>
        <p class="mono aep-drawer__count" data-drawer-count>Empty</p>
        <button class="aep-drawer__close" type="button" data-cart-dismiss aria-label="Close cart">
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3L3 13"/></svg>
        </button>
      </header>

      <div class="aep-drawer__body" data-drawer-body>
        <ul class="aep-drawer__lines" data-drawer-lines></ul>
        <div class="aep-drawer__empty" data-drawer-empty hidden>
          <p class="aep-drawer__emptyLine">Nothing in the cart yet.</p>
          <a class="aep-drawer__emptyLink" href="${urls.shop}">
            <span>The Gem Collection</span>
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M1 8h13M9 3l5 5-5 5"/></svg>
          </a>
        </div>
      </div>

      <footer class="aep-drawer__foot" data-drawer-foot>
        <div class="aep-drawer__sum">
          <span class="label">Subtotal</span>
          <span class="mono aep-drawer__total" data-drawer-total>$0.00</span>
        </div>
        <p class="aep-drawer__ship">Shipping and duties calculated at checkout.</p>
        <button class="aep-drawer__checkout" type="button" data-cart-checkout>Checkout</button>
        <a class="aep-drawer__view" href="${urls.cart}">View cart</a>
        <p class="mono aep-drawer__status" role="status" data-drawer-status></p>
      </footer>
    </div>
  `;

  document.body.appendChild(dlg);

  panel = dlg.querySelector('.aep-drawer__panel');
  listEl = dlg.querySelector('[data-drawer-lines]');
  emptyEl = dlg.querySelector('[data-drawer-empty]');
  countEl = dlg.querySelector('[data-drawer-count]');
  subEl = dlg.querySelector('[data-drawer-total]');
  statusEl = dlg.querySelector('[data-drawer-status]');
  footEl = dlg.querySelector('[data-drawer-foot]');

  // Escape. The default would close() instantly and skip the exit, so we
  // decline it and run the same close path a click on the scrim runs.
  dlg.addEventListener('cancel', (e) => { e.preventDefault(); closeCart(); });

  dlg.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-dismiss]')) { closeCart(); return; }

    // A link out of the drawer: leave the top layer BEFORE the page
    // transition veil mounts, or the panel paints on top of it — the veil
    // is an ordinary element and cannot outrank a modal dialog.
    const a = e.target.closest('a[href]');
    if (a && !e.defaultPrevented) closeCart({ instant: true });
  });

  bindLineControls();
  bindCheckout();
}

/* ---------- Line controls ----------
   One delegated listener for the whole list, so appending a line costs
   no bookkeeping. */
function bindLineControls() {
  listEl.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-act]');
    if (!btn) return;
    const li = btn.closest('.aep-line');
    if (!li) return;

    const id = li.dataset.id;
    const act = btn.dataset.act;

    // A rejected mutation republishes the store's last known truth, which
    // repaints whatever the optimistic leave() animation took out — the
    // line the server still holds comes back rather than silently
    // vanishing from the screen but not the order.
    const recover = () => {
      publish('sync');
      if (statusEl) statusEl.textContent = 'The cart did not respond. Nothing was changed.';
    };

    if (act === 'remove') { leave(li, () => removeItem(id).catch(recover)); return; }

    const line = readCart().items.find((l) => l.id === id);
    if (!line) return;
    const next = line.qty + (act === 'plus' ? 1 : -1);
    if (next < 1) { leave(li, () => removeItem(id).catch(recover)); return; }
    setQty(id, next).catch(recover);
  });
}

/**
 * Collapse a line to nothing, then commit the removal. The height is a
 * `grid-template-rows: 1fr → 0fr` transition rather than a measured pixel
 * height: no layout read, no hardcoded row height, and it stays correct
 * when a long product name wraps to two lines.
 *
 * The node comes out BEFORE the store does, and the order matters: commit()
 * publishes, render() runs synchronously inside it, and a node still in the
 * list at that moment is one render() has to decide about mid-animation.
 * Taking it out first means there is nothing to decide.
 */
function leave(li, commit) {
  if (reduced()) { li.remove(); commit(); return; }

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    li.remove();
    commit();
  };

  li.classList.add('is-leaving');
  li.addEventListener('transitionend', (e) => {
    if (e.target !== li || e.propertyName !== 'grid-template-rows') return;
    finish();
  });
  // The transition can be interrupted — a background tab never runs a
  // frame, and transitionend never fires. The removal is not optional, so
  // it also happens on a timer, and `done` makes the two idempotent.
  setTimeout(finish, 600);
}

function bindCheckout() {
  const btn = dlg.querySelector('[data-cart-checkout]');
  btn.addEventListener('click', () => {
    // With a merchant behind it, the button goes where it says.
    if (urls.checkout) {
      window.location.assign(urls.checkout);
      return;
    }
    // Honest, and permanent: no spinner, no "processing", nothing that
    // implies a transaction is being attempted.
    statusEl.textContent = 'Checkout is not connected on this build.';
    btn.classList.add('is-declined');
    setTimeout(() => btn.classList.remove('is-declined'), 1200);
  });
}

/* ---------- Rendering ----------
   Diffed by id, not rebuilt. Rebuilding would be four lines shorter and
   would also throw away the leave animation mid-flight, drop focus out of
   the panel on every quantity press, and re-run the open stagger on a
   cart that has merely changed by one. */

function lineHTML(line, i) {
  const media = line.image
    ? `<img src="${esc(line.image)}" alt="" width="128" height="128" loading="lazy" decoding="async">`
    : '';
  const meta = [line.size, line.dims].filter(Boolean).join(' · ');

  return `
    <li class="aep-line" data-id="${esc(line.id)}" style="--i:${i}">
      <div class="aep-line__in">
        <div class="aep-line__media">${media}</div>
        <div class="aep-line__body">
          <p class="aep-line__name">${
            line.url ? `<a href="${esc(line.url)}">${esc(line.title)}</a>` : esc(line.title)
          }</p>
          <p class="mono aep-line__meta">${esc(meta)}</p>
          <div class="aep-line__row">
            <div class="aep-qty">
              <button class="aep-qty__btn" type="button" data-act="minus"
                      aria-label="Decrease quantity of ${esc(line.title)}">–</button>
              <span class="mono aep-qty__n" data-line-qty aria-live="polite">${line.qty}</span>
              <button class="aep-qty__btn" type="button" data-act="plus"
                      aria-label="Increase quantity of ${esc(line.title)}">+</button>
            </div>
            <p class="mono aep-line__price" data-line-price>${money(lineTotal(line))}</p>
          </div>
          <button class="aep-line__remove" type="button" data-act="remove"
                  aria-label="Remove ${esc(line.title)} from cart">Remove</button>
        </div>
      </div>
    </li>
  `;
}

function render(cart) {
  if (!dlg) return;

  const byId = new Map(cart.items.map((l) => [l.id, l]));

  // Gone. No exemption for a line that is mid-collapse: leave() takes its
  // own node out before it commits, so anything still here whose id has
  // left the store was removed by something else — another tab, most
  // likely — and stranding it would leave a line on screen that the cart
  // no longer contains.
  [...listEl.children].forEach((li) => {
    if (!byId.has(li.dataset.id)) li.remove();
  });

  // New and changed
  cart.items.forEach((line, i) => {
    let li = listEl.querySelector(`.aep-line[data-id="${CSS.escape(line.id)}"]`);

    if (!li) {
      const frag = document.createElement('template');
      frag.innerHTML = lineHTML(line, i).trim();
      li = frag.content.firstElementChild;
      // A line arriving into an open drawer opens from nothing, the exact
      // inverse of leave(). One frame at 0fr, then release.
      const animate = open && !reduced();
      if (animate) li.classList.add('is-entering');
      listEl.appendChild(li);
      if (animate) requestAnimationFrame(() => li.classList.remove('is-entering'));
    } else {
      li.style.setProperty('--i', String(i));
      const q = li.querySelector('[data-line-qty]');
      const p = li.querySelector('[data-line-price]');
      if (q) q.textContent = String(line.qty);
      if (p) p.textContent = money(lineTotal(line));
    }
  });

  const empty = cart.item_count === 0;
  emptyEl.hidden = !empty;
  footEl.hidden = empty;
  countEl.textContent = empty
    ? 'Empty'
    : `${cart.item_count} item${cart.item_count === 1 ? '' : 's'}`;
  subEl.textContent = money(cart.total_price);
  if (empty) statusEl.textContent = '';
}

/* ---------- Open / close ---------- */

/**
 * @param {object}       [opts]
 * @param {Element|null} [opts.returnTo]  what to focus when the drawer
 *   closes. Needed because the PDP disables its Add to cart button for the
 *   duration of the add, and disabling the focused element hands focus to
 *   <body> — so by the time we get here, activeElement has already
 *   forgotten where the customer was. The caller has not.
 */
export function openCart({ returnTo } = {}) {
  build();
  if (open) { render(readCart()); return; }

  opener = returnTo || document.activeElement;
  render(readCart());

  dlg.showModal();
  document.documentElement.classList.add('aep-cart-open');

  // The panel is at translateX(100%) until `is-open` lands. Reading a
  // layout property in between is what guarantees the browser has a
  // from-state to transition out of rather than one merged frame.
  void dlg.offsetWidth;
  open = true;
  dlg.classList.add('is-open', 'is-lines-in');

  // showModal() focuses the first focusable in DOM order, which is the
  // scrim's sibling — name the target explicitly instead.
  const first = dlg.querySelector('.aep-drawer__close');
  if (first) first.focus({ preventScroll: true });
}

/**
 * @param {object}  [opts]
 * @param {boolean} [opts.instant]  skip the exit — used when a link inside
 *   the drawer is about to navigate and the panel must leave the top layer
 *   this frame.
 */
export function closeCart({ instant = false } = {}) {
  if (!dlg || !open) return;
  open = false;
  dlg.classList.remove('is-open');

  const finish = () => {
    clearTimeout(closeTimer);
    closeTimer = 0;
    dlg.classList.remove('is-lines-in');
    if (dlg.open) dlg.close();
    document.documentElement.classList.remove('aep-cart-open');
    if (opener && opener.isConnected && typeof opener.focus === 'function') {
      opener.focus({ preventScroll: true });
    }
    opener = null;
  };

  if (instant || reduced()) { finish(); return; }

  // transitionend is the accurate signal; the timer is the one that has to
  // be right when the tab is backgrounded mid-close and no frame ever runs.
  panel.addEventListener('transitionend', function done(e) {
    if (e.target !== panel || e.propertyName !== 'transform') return;
    panel.removeEventListener('transitionend', done);
    finish();
  });
  closeTimer = setTimeout(finish, 600);
}

export function toggleCart() {
  if (open) closeCart(); else openCart();
}

/* ============================================================
   Boot
   ============================================================ */

let booted = false;

/**
 * Wire the header cart to the drawer and keep both in step with the store.
 * Called from initShell(), so every page gets it without touching markup.
 */
export function initCart() {
  if (booted) return;
  booted = true;

  document.querySelectorAll('.header__cart').forEach((a) => {
    a.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      // Before transition.js's document-level listener sees it: this is a
      // panel, not a navigation. Without JS the href still goes to the page.
      e.preventDefault();
      openCart();
    });
  });

  document.addEventListener('cart:updated', (e) => render(e.detail));

  if (SHOPIFY) {
    // Fetch before the boot publish, never after an empty one: the header
    // count was rendered by Liquid and is already correct, so the one
    // wrong thing to do here is stamp a zero over it while the request is
    // in flight. If the read fails the Liquid number simply stands.
    refreshShopify().then(() => publish('boot')).catch(() => {});

    // Another tab changed the cart. No storage event crosses tabs for a
    // server cart, but coming back to a tab is the moment staleness is
    // seen, so it is the moment to revalidate.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      refreshShopify().then(() => publish('sync')).catch(() => {});
    });
    return;
  }

  watchStorage();
  publish('boot');
}

export default initCart;
